import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authService } from "../../services/auth.service";
import useAuth from "../../hooks/useAuth";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { landingPathForRole } from "../../utils/navReturn";
import PortalSignInLayout from "../../components/auth/PortalSignInLayout";
import "../../styles/auth.css";

export default function RepoAdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);
    setError("");

    try {
      // 1. Login API
      const data = await authService.repoAdminLogin(form);

      // 2. Validate login response
      if (!data?.user || !data?.token) {
        throw new Error("Invalid login response");
      }

      // 3. Make sure this is actually a repo admin
      if (data.user.role !== "REPO_ADMIN") {
        throw new Error("Only repo admin can login here.");
      }

      console.log("[REPO ADMIN LOGIN] Login successful:", {
        userId: data.user?.id || data.user?._id,
        role: data.user?.role,
      });

      // 4. IMPORTANT:
      // Wait until AuthContext finishes saving the user,
      // token and effective permissions.
      const permissions = await login({
        user: data.user,
        token: data.token,
      });

      console.log("[REPO ADMIN LOGIN] Permissions loaded:", permissions);

      // 5. Determine landing page AFTER permissions are loaded
      const landingPath = landingPathForRole(
        data.user.role,
        Array.isArray(permissions) ? permissions : []
      );

      console.log("[REPO ADMIN LOGIN] Navigating to:", landingPath);

      // 6. Navigate only after login + permissions are completely ready
      navigate(landingPath, {
        replace: true,
      });
    } catch (err) {
      console.error(
        "[REPO ADMIN LOGIN ERROR]",
        err?.response?.data || err?.message || err
      );

      setError(
        getApiErrorMessage(
          err,
          "Repo admin login failed"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalSignInLayout
      breadcrumb={[
        {
          label: "Repo Company Admin Login",
        },
      ]}
      title="Repo company admin"
      subtitle="Manage your recovery company — staff, assignments, reports, and daily operations from one secure workspace."
      perks={[
        "Company operations",
        "Staff and assignments",
        "Reports and records",
      ]}
    >
      <form
        onSubmit={handleSubmit}
        className="portal-signin-form"
      >
        <div className="form-group">
          <label htmlFor="email">
            Email
          </label>

          <input
            id="email"
            type="email"
            name="email"
            placeholder="Repo admin email"
            value={form.email}
            onChange={handleChange}
            required
            autoComplete="email"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">
            Password
          </label>

          <input
            id="password"
            type="password"
            name="password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            required
            autoComplete="current-password"
            disabled={loading}
          />
        </div>

        {error && (
          <p className="auth-message error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="portal-signin-submit"
          disabled={loading}
        >
          {loading ? "Please wait…" : "Login"}
        </button>

        <div className="portal-signin-links">
          <p>
            <Link to="/register">
              New company? Register via APK
            </Link>
          </p>

          <p>
            <Link to="/forgot-password">
              Forgot password?
            </Link>
          </p>
        </div>
      </form>
    </PortalSignInLayout>
  );
}